import { atom, type Setter } from 'jotai';

import type { CompareResult } from '@/lib/analyses/compare/availability';
import type { CompareFormValues } from '@/lib/analyses/compare/formSchema';

export interface DeviationTracker {
  toastId: string | number;
  batchId: string;
  projectId: string;
  sourceLayerId: string;
  sourceLayerName: string;
  /** Snapshot of layer IDs that were already derived from `sourceLayerId` at
   * the moment the user clicked Run. The "new layer" produced by this run is
   * any layer with `metadata.derivedFrom === sourceLayerId` whose ID is NOT
   * in this snapshot. We previously compared `layer.createdAt` (server clock)
   * to a `Date.now()` captured client-side, which fails under any clock skew
   * — toast would either spin forever (client ahead of server) or resolve
   * immediately by matching a stale pre-existing derived layer (client behind). */
  existingDerivedLayerIds: string[];
  /** Which analysis produced this tracker. Picks the toast strings used on
   * completion / failure; `'deviation'` and `'surfaceDifference'` share the
   * same matching logic (both resolve on a new layer whose
   * `metadata.derivedFrom` is the source layer id). Defaults to
   * `'deviation'` when absent.
   *
   * `'clearance'` is the CLEARANCE run started from `RunClearanceDialog`. It
   * rides this same tracker, but its resolution is STRICTER than the other
   * two: postprocess stamps the very same `metadata.derivedFrom` on every
   * analysis output of a given cloud, so `derivedFrom` + the snapshot alone
   * resolved a clearance toast against ANOTHER run's layer while this run was
   * still on ECS. `useDeviationProgress` therefore additionally requires
   * `metadata.batchId === batchId` and this batch's POST (postprocess) job to
   * be PROCESSED before it calls a clearance run complete.
   *
   * `'layerStack'` is the LAYER_STACK run started from `RunLayerStackDialog`.
   * It resolves on `metadata.batchId` ALONE, never on `derivedFrom`: a stack
   * has N source surfaces and no single "source layer" to derive from, and
   * `postprocess.ts` stamps no `derivedFrom` on the ONE result layer it mints
   * (spec §6.2). Like clearance it also waits for this batch's POST job to be
   * PROCESSED, so the toast resolves when postprocess is finished rather than
   * when it has merely inserted the row.
   *
   * `'sectionOverlays'` is the SECTION_OVERLAYS re-sample started from the
   * dock's Overlays popover. It is the odd one out: it mints NO layer at all —
   * it rewrites two byproducts on the RUN's existing batch and stamps
   * `metadata.sectionOverlays` back onto the same result layer. So it resolves
   * on its own job's status alone (`type === 'SECTION_OVERLAYS'` in
   * `tracker.batchId`), and the layer scan below never runs for it.
   *
   * `'map2d'` — a MAP_2D regeneration from the layer menu; resolves on its own job, mints no layer. */
  kind?: 'deviation' | 'surfaceDifference' | 'clearance' | 'layerStack' | 'sectionOverlays' | 'map2d';
  /**
   * `'sectionOverlays'` only: the batch the OVERLAYS were sampled onto — the
   * original run's batch, not the SECTION_OVERLAYS batch in `batchId`.
   *
   * The two are different batches, and the chart reads the sampled file
   * through `useByproductUrl(runBatchId, …)`. Without invalidating that key on
   * completion the new file is registered server-side and the open dock keeps
   * showing the previous one — for as long as the tab stays open, since
   * `batchByproducts` is cached with no stale time.
   */
  runBatchId?: string;

  /**
   * Compare surfaces (spec §4.3, §12.7): a paired submit registers ONE tracker
   * per part, all sharing `toastId` and `groupId`. Each part resolves on its
   * own batch exactly as an ungrouped run of its `kind` would, but instead of
   * settling the toast it records `partOutcome`; the toast is settled once
   * every part of the group has one. Absent on every ungrouped tracker.
   */
  groupId?: string;
  /** Which result of the comparison this tracker follows. */
  part?: CompareResult;
  /** The `project` i18n key naming the part in the toast ("Cut & fill"). */
  partLabelKey?: string;
  /** The combined PDF's batch; its report tracker is dropped when no part succeeded. */
  reportBatchId?: string;
  /** Set by `useDeviationProgress` when this part finished; the group settles when all have one. */
  partOutcome?: { status: 'done'; layerId?: string } | { status: 'failed'; reason: string };
  /**
   * The server could not tell whether this part's preprocess started
   * (`startComparison.startUncertain`, R10). It is tracked like any part, and
   * additionally failed when its batch turns FAILED with no job ever started
   * — a preprocess failure no job list can show.
   */
  startUncertain?: boolean;
  /**
   * When the uncertain part was registered (epoch ms). A preprocess that
   * never ran leaves its batch PROCESSING with no job at all, so past
   * `UNCERTAIN_START_TIMEOUT_MS` with still no job the part is failed.
   */
  startUncertainAt?: number;
}

/** How long an uncertain start may show no job before its part is failed (round 3). */
export const UNCERTAIN_START_TIMEOUT_MS = 5 * 60 * 1000;

export const deviationTrackingAtom = atom<DeviationTracker[]>([]);

/**
 * The submitted form values of each paired run, by `groupId`, so a partial
 * failure's Retry action can reopen the dialog with them. In memory only: the
 * toast that offers Retry does not outlive the tab either.
 */
export const comparisonRetryValuesAtom = atom<Record<string, Partial<CompareFormValues>>>({});

const PART_KIND: Record<CompareResult, NonNullable<DeviationTracker['kind']>> = {
  cutFill: 'surfaceDifference',
  deviation: 'deviation',
};

export const COMPARE_PART_LABEL_KEYS: Record<CompareResult, string> = {
  cutFill: 'compareSurfaces.part.cutFill',
  deviation: 'compareSurfaces.part.deviation',
};

export interface ComparisonTrackerMember {
  part: CompareResult;
  batchId: string;
  sourceLayerId: string;
  sourceLayerName: string;
  existingDerivedLayerIds: string[];
  /**
   * Set when this part's start mutation failed (ruling R17). The part is
   * registered already failed with this reason and is never polled, so the
   * group's end toast still reports it ("finished with 1 failure").
   */
  failedToStart?: { reason: string };
  /** The server could not tell whether this part started (R10): tracked normally, never pre-failed. */
  startUncertain?: boolean;
}

/**
 * Registers a paired Compare surfaces submit: one tracker per started part,
 * all on the one `toastId` the dialog opened, plus the retry values. `set` is
 * jotai's setter (`useStore().set`, or the one `useAtomCallback` hands out).
 */
export function registerComparisonTrackers(
  set: Setter,
  args: {
    toastId: string | number;
    projectId: string;
    groupId: string;
    members: ComparisonTrackerMember[];
    reportBatchId?: string;
    retryValues: Partial<CompareFormValues>;
  },
): void {
  const { toastId, projectId, groupId, members, reportBatchId, retryValues } = args;
  set(deviationTrackingAtom, (prev) => [
    ...prev,
    ...members.map(
      (m): DeviationTracker => ({
        toastId,
        projectId,
        batchId: m.batchId,
        sourceLayerId: m.sourceLayerId,
        sourceLayerName: m.sourceLayerName,
        existingDerivedLayerIds: m.existingDerivedLayerIds,
        kind: PART_KIND[m.part],
        groupId,
        part: m.part,
        partLabelKey: COMPARE_PART_LABEL_KEYS[m.part],
        reportBatchId,
        ...(m.failedToStart && { partOutcome: { status: 'failed' as const, reason: m.failedToStart.reason } }),
        ...(m.startUncertain && !m.failedToStart && { startUncertain: true, startUncertainAt: Date.now() }),
      }),
    ),
  ]);
  set(comparisonRetryValuesAtom, (prev) => ({ ...prev, [groupId]: retryValues }));
}
